import { FC, useCallback, useMemo, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { Block, Visibility } from '@mui/icons-material'
import { Box, TableContainer } from '@mui/material'

import { GRTable, GRTableColumn, SortOrder } from '../../../../../components/GRTable/GRTable'
import { GRTooltip } from '../../../../../components/GRTooltip/GRTooltip'
import { htmlDecode } from '../../../../../helpers/dom'
import { useViewTransitionNavigate } from '../../../../../hooks/useViewTransitionNavigate'
import { ActionButton } from '../../../../components/ActionButton/ActionButton'
import { MarketBlueprintUsage } from '../../hooks/useMarketBlueprintUsage'

type MarketLevelBlueprintsOverviewTableProps = {
  usages: MarketBlueprintUsage[]
  isLoading?: boolean
}

/**
 * Table listing all Market Level Blueprints with usage counts (linked games and
 * linked game-level blueprints) for data sanity checking. Clicking a row opens
 * the detail view for the blueprint.
 */
export const MarketLevelBlueprintsOverviewTable: FC<MarketLevelBlueprintsOverviewTableProps> = ({ usages, isLoading }) => {
  const { t } = useTranslation()
  const navigate = useViewTransitionNavigate()
  const containerRef = useRef(null)

  const handleOpenBlueprint = useCallback(
    (usage: MarketBlueprintUsage) => {
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
      navigate(`/internal/live-events-tracking/market-level-blueprints/overview/${usage.marketArchetype.id}`)
    },
    [navigate]
  )

  const customProps = useMemo(() => ({ handleOpenBlueprint }), [handleOpenBlueprint])

  const tableColumns: GRTableColumn<MarketBlueprintUsage, typeof customProps>[] = useMemo(
    () => [
      {
        labelAccessor: () => <Trans i18nKey="internal-common:name" />,
        accessor: ({ row }) => row.marketArchetype.names.en,
        cellProps: { sx: { textAlign: 'left' } },
        headerCellProps: { sx: { textAlign: 'left', minWidth: 200, px: 2 } },
        sortable: true,
        sortAccessor: ({ row }) => row.marketArchetype.names.en || '',
        defaultSortOrder: SortOrder.ASC,
      },
      {
        labelAccessor: () => <Box sx={{ pl: 2 }}>{<Trans i18nKey="internal-live-events:description" />}</Box>,
        accessor: ({ row }) => {
          const descriptionHtml = row.marketArchetype.descriptions?.en || ''
          if (!descriptionHtml) return ''

          const descriptionText = htmlDecode(descriptionHtml) || ''

          return (
            <GRTooltip content={<Box sx={{ maxWidth: 400 }} dangerouslySetInnerHTML={{ __html: descriptionHtml }} />} disableInteractive={false}>
              <Box
                sx={{
                  display: 'block',
                  pl: 2,
                  maxWidth: 300,
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {descriptionText}
              </Box>
            </GRTooltip>
          )
        },
        cellProps: { sx: { textAlign: 'left' } },
        headerCellProps: { sx: { textAlign: 'left' } },
      },
      {
        labelAccessor: () => <Trans i18nKey="live-events:live_event_evolution_type_discontinued" />,
        accessor: ({ row }) => (row.marketArchetype.discontinued ? <Block color="error" /> : undefined),
        cellProps: { sx: { textAlign: 'center' } },
        headerCellProps: { sx: { textAlign: 'center', width: 110, px: 2 } },
        sortable: true,
        sortAccessor: ({ row }) => (row.marketArchetype.discontinued ? 1 : 0),
      },
      {
        labelAccessor: () => <Trans i18nKey="internal-live-events:linked_games" />,
        accessor: ({ row }) => (
          <Box component="span" sx={{ color: row.games.length === 0 ? 'warning.main' : 'text.primary' }}>
            {row.games.length}
          </Box>
        ),
        cellProps: { sx: { textAlign: 'center' } },
        headerCellProps: { sx: { textAlign: 'center', width: 120, px: 2 } },
        sortable: true,
        sortOrder: SortOrder.DESC,
        sortAccessor: ({ row }) => row.games.length,
      },
      {
        labelAccessor: () => <Trans i18nKey="internal-live-events:linked_game_blueprints" />,
        accessor: ({ row }) => (
          <Box component="span" sx={{ color: row.gameBlueprintCount === 0 ? 'warning.main' : 'text.primary' }}>
            {row.gameBlueprintCount}
          </Box>
        ),
        cellProps: { sx: { textAlign: 'center' } },
        headerCellProps: { sx: { textAlign: 'center', width: 160, px: 2 } },
        sortable: true,
        sortAccessor: ({ row }) => row.gameBlueprintCount,
      },
      {
        labelAccessor: () => <Trans i18nKey="internal-live-events:actions" />,
        headerCellProps: { sx: { width: 90, px: 2 } },
        accessor: ({ row, customTableProps }) => (
          <ActionButton onClick={() => customTableProps?.handleOpenBlueprint(row)}>
            <Visibility fontSize="small" />
          </ActionButton>
        ),
      },
    ],
    []
  )

  const [columns, setColumns] = useState<GRTableColumn<MarketBlueprintUsage, typeof customProps>[]>(tableColumns)

  const handleColumnsUpdate = useCallback((updatedColumns: GRTableColumn<MarketBlueprintUsage, typeof customProps>[]) => {
    setColumns(updatedColumns)
  }, [])

  return (
    <TableContainer component={Box} ref={containerRef}>
      <GRTable
        rows={usages}
        columns={columns}
        rowIdKey={(row) => row.marketArchetype.id}
        scroller={containerRef}
        isLoading={isLoading}
        onColumnsUpdated={handleColumnsUpdate}
        noRowsLabel={t('internal-live-events:no_market_level_blueprints_found')}
        customProps={customProps}
        gridlines
        striped
        hoverable
      />
    </TableContainer>
  )
}
